/**
 * StudioEventStreamService - Phase 3C SceneStream Push
 *
 * Single subscription service for mission timeline events via Socket.IO.
 * Eliminates REST polling and 429 rate limit errors.
 *
 * Non-negotiables (from Dana's spec):
 * 1. One subscription per browser session (singleton pattern)
 * 2. Dedup by eventId
 * 3. Backfill on reconnect using after=<lastEventId>
 * 4. REST calls only on: initial load, session switch, reconnect, "load older"
 * 5. No new UI - wiring only
 *
 * Architecture:
 * - Connects to /studio namespace (dedicated for mission events, separate from ABDA)
 * - Subscribes to mission:event channel
 * - Dispatches events to registered listeners (card rendering)
 * - Handles reconnect with backfill
 * - Applies exponential backoff if REST backfill hits 429
 */

import { io, Socket } from 'socket.io-client';

/**
 * Mission timeline event from backend (matches MissionTimelineEvent type)
 */
export interface MissionTimelineEvent {
    id: string;
    missionId: string;
    t: string; // Event type: mission.start, mission.finish, pr.open, etc.
    severity: 'info' | 'warn' | 'error';
    title: string;
    message?: string;
    meta?: Record<string, unknown>;
    createdAt: string;
}

export type MissionEventListener = (event: MissionTimelineEvent) => void;

interface StudioEventStreamConfig {
    baseUrl?: string;
    wsPath?: string;
    namespace?: string;
    jwt?: string;
    debug?: boolean;
}

interface ConnectionState {
    connected: boolean;
    lastEventId: string | null;
    reconnectAttempts: number;
}

/**
 * Polling mode for mutual exclusion with SceneStream
 * - 'disabled': SceneStream is connected, polling should be off
 * - 'allowed': SceneStream failed after timeout, polling is allowed as fallback
 * - 'pending': Initial state, waiting for first connection attempt result
 */
export type PollingMode = 'disabled' | 'allowed' | 'pending';

export type PollingModeListener = (mode: PollingMode) => void;

/**
 * StudioEventStreamService - Singleton service for mission event streaming
 */
class StudioEventStreamServiceImpl {
    private socket: Socket | null = null;
    private listeners: Set<MissionEventListener> = new Set();
    private seenEventIds: Set<string> = new Set();
    private state: ConnectionState = {
        connected: false,
        lastEventId: null,
        reconnectAttempts: 0,
    };
    private config: StudioEventStreamConfig = {};
    private debug = false;
    private didLogConnect = false; // Phase 3C debug: log connect() once per page load

    // Polling gate state for mutual exclusion
    private pollingMode: PollingMode = 'pending';
    private pollingModeListeners: Set<PollingModeListener> = new Set();
    private fallbackTimerId: ReturnType<typeof setTimeout> | null = null;
    private connectionAttemptStarted = false;

    // Reconnect configuration
    private readonly MAX_RECONNECT_ATTEMPTS = 10;
    private readonly BASE_RECONNECT_DELAY_MS = 2000;
    private readonly MAX_RECONNECT_DELAY_MS = 60000;

    // Fallback configuration: allow polling after this many seconds without successful connection
    private readonly FALLBACK_TIMEOUT_MS = 15000; // 15 seconds
    private readonly FALLBACK_ATTEMPT_THRESHOLD = 3; // or after 3 failed attempts

    // Dedup configuration - keep last N event IDs to prevent duplicates
    private readonly MAX_SEEN_EVENT_IDS = 1000;

    private log(...args: unknown[]) {
        if (this.debug) {
            console.log('[StudioEventStream]', ...args);
        }
    }

    /**
     * Initialize the service with configuration.
     * Call this once on app startup.
     */
    configure(config: StudioEventStreamConfig): void {
        this.config = config;
        this.debug = config.debug ?? false;
        this.log('Configured with:', {
            baseUrl: config.baseUrl,
            namespace: config.namespace,
        });
    }

    /**
     * Connect to the Socket.IO server and subscribe to mission events.
     * Safe to call multiple times - will reuse existing connection.
     */
    connect(): void {
        // Phase 3C Debug: Log connect() invocation when VITE_SCENESTREAM_DEBUG=true
        // This is dev-only and will not ship to production
        const env = import.meta.env as Record<string, string | undefined>;
        const sceneStreamDebug = env['VITE_SCENESTREAM_DEBUG'] === 'true';

        if (sceneStreamDebug && !this.didLogConnect) {
            this.didLogConnect = true;
            const baseUrl =
                this.config.baseUrl ||
                env['VITE_HIVECORE_API_URL'] ||
                env['VITE_BACKEND_URL'] ||
                'https://hivecore-backend-production.up.railway.app';
            const namespace = this.config.namespace || env['VITE_HIVECORE_NS'] || '/studio';
            console.log(
                '[Phase3C] StudioEventStreamService.connect() invoked',
                '\n  targetOrigin:',
                baseUrl,
                '\n  namespace:',
                namespace
            );
        }

        // If a socket instance already exists (connected or in-flight), reuse it.
        // This avoids creating multiple sockets when multiple subscribers call subscribe()
        // during initial connection establishment.
        if (this.socket) {
            if (this.socket.connected) {
                this.log('Already connected, skipping');
            } else {
                this.log('Socket already created (connecting), reusing');
            }
            return;
        }

        const baseUrl =
            this.config.baseUrl ||
            env['VITE_HIVECORE_API_URL'] ||
            env['VITE_BACKEND_URL'] ||
            'https://hivecore-backend-production.up.railway.app';
        const wsPath = this.config.wsPath || env['VITE_HIVECORE_WS_PATH'] || '/socket.io';
        const namespace = this.config.namespace || env['VITE_HIVECORE_NS'] || '/studio';

        const socketUrl = `${baseUrl.replace(/\/+$/, '')}${namespace}`;

        this.log('Connecting to:', socketUrl);

        this.socket = io(socketUrl, {
            path: wsPath,
            transports: ['websocket', 'polling'],
            withCredentials: true,
            auth: {
                token: this.config.jwt || '',
            },
            reconnection: true,
            reconnectionDelay: this.BASE_RECONNECT_DELAY_MS,
            reconnectionAttempts: this.MAX_RECONNECT_ATTEMPTS,
        });

        // Phase 3C Mutual Exclusion: Start fallback timer on first connection attempt
        if (!this.connectionAttemptStarted) {
            this.connectionAttemptStarted = true;
            this.startFallbackTimer();
        }

        this.setupEventHandlers();
    }

    private setupEventHandlers(): void {
        if (!this.socket) return;

        this.socket.on('connect', () => {
            this.log('Connected, subscribing to mission events');
            this.state.connected = true;
            this.state.reconnectAttempts = 0;

            // Phase 3C Mutual Exclusion: Disable polling when connected
            this.clearFallbackTimer();
            this.setPollingMode('disabled');

            // Subscribe to mission events with backfill from last seen event
            this.socket?.emit('mission:subscribe', {
                afterEventId: this.state.lastEventId,
            });
        });

        this.socket.on('disconnect', reason => {
            this.log('Disconnected:', reason);
            this.state.connected = false;
            // Note: Don't immediately enable polling on disconnect.
            // Socket.IO will attempt to reconnect automatically.
            // Only enable polling after fallback timeout or max attempts.
        });

        this.socket.on('connect_error', err => {
            console.warn('[StudioEventStream] Connection error:', err.message);
            this.state.reconnectAttempts++;

            // Phase 3C Mutual Exclusion: Enable polling fallback after threshold
            if (this.state.reconnectAttempts >= this.FALLBACK_ATTEMPT_THRESHOLD) {
                this.log('Fallback attempt threshold reached, enabling polling');
                this.setPollingMode('allowed');
            }
        });

        // Handle subscription confirmation
        this.socket.on(
            'mission:subscribed',
            (data: { lastEventId: string | null; backfillCount: number }) => {
                this.log('Subscribed to mission events:', data);
                if (data.lastEventId && !this.state.lastEventId) {
                    this.state.lastEventId = data.lastEventId;
                }
            }
        );

        // Handle mission events
        this.socket.on('mission:event', (event: MissionTimelineEvent) => {
            this.handleMissionEvent(event);
        });
    }

    private handleMissionEvent(event: MissionTimelineEvent): void {
        // Dedup by eventId
        if (this.seenEventIds.has(event.id)) {
            this.log('Duplicate event, skipping:', event.id);
            return;
        }

        // Add to seen set (bounded)
        this.seenEventIds.add(event.id);
        if (this.seenEventIds.size > this.MAX_SEEN_EVENT_IDS) {
            // Remove oldest entries (convert to array, slice, convert back)
            const arr = Array.from(this.seenEventIds);
            this.seenEventIds = new Set(arr.slice(-this.MAX_SEEN_EVENT_IDS / 2));
        }

        // Update last event ID for reconnect backfill
        this.state.lastEventId = event.id;

        this.log('Received mission event:', event.t, event.id);

        // Dispatch to all listeners
        for (const listener of this.listeners) {
            try {
                listener(event);
            } catch (err) {
                console.error('[StudioEventStream] Listener error:', err);
            }
        }
    }

    /**
     * Subscribe to mission events.
     * Returns an unsubscribe function.
     */
    subscribe(listener: MissionEventListener): () => void {
        this.listeners.add(listener);
        this.log('Listener added, total:', this.listeners.size);

        // Auto-connect if not already connected
        if (!this.socket?.connected) {
            this.connect();
        }

        return () => {
            this.listeners.delete(listener);
            this.log('Listener removed, total:', this.listeners.size);
        };
    }

    /**
     * Disconnect from the Socket.IO server.
     * Call this on app unmount or session switch.
     */
    disconnect(): void {
        if (this.socket) {
            this.socket.emit('mission:unsubscribe');
            this.socket.disconnect();
            this.socket = null;
        }
        this.state.connected = false;
        this.log('Disconnected');
    }

    /**
     * Reset state for session switch.
     * Clears seen event IDs and last event ID.
     */
    resetForSessionSwitch(): void {
        this.seenEventIds.clear();
        this.state.lastEventId = null;
        this.log('Reset for session switch');
    }

    /**
     * Check if connected to the Socket.IO server.
     */
    isConnected(): boolean {
        return this.state.connected;
    }

    /**
     * Get the last seen event ID (for REST backfill on reconnect).
     */
    getLastEventId(): string | null {
        return this.state.lastEventId;
    }

    /**
     * Get connection state for debugging.
     */
    getState(): ConnectionState {
        return { ...this.state };
    }

    /**
     * Phase 3C Mutual Exclusion: Get current polling mode.
     * Components should check this to decide whether to start polling intervals.
     */
    getPollingMode(): PollingMode {
        return this.pollingMode;
    }

    /**
     * Phase 3C Mutual Exclusion: Subscribe to polling mode changes.
     * Returns an unsubscribe function.
     */
    subscribePollingMode(listener: PollingModeListener): () => void {
        this.pollingModeListeners.add(listener);
        // Immediately notify with current mode
        listener(this.pollingMode);
        return () => {
            this.pollingModeListeners.delete(listener);
        };
    }

    /**
     * Internal: Set polling mode and notify listeners.
     */
    private setPollingMode(mode: PollingMode): void {
        if (this.pollingMode === mode) return;
        this.log('Polling mode changed:', this.pollingMode, '->', mode);
        this.pollingMode = mode;
        for (const listener of this.pollingModeListeners) {
            try {
                listener(mode);
            } catch (err) {
                console.error('[StudioEventStream] Polling mode listener error:', err);
            }
        }
    }

    /**
     * Internal: Start fallback timer when connection attempt begins.
     * If connection doesn't succeed within FALLBACK_TIMEOUT_MS, allow polling.
     */
    private startFallbackTimer(): void {
        if (this.fallbackTimerId) return; // Already running
        this.fallbackTimerId = setTimeout(() => {
            if (!this.state.connected) {
                this.log('Fallback timeout reached, enabling polling');
                this.setPollingMode('allowed');
            }
            this.fallbackTimerId = null;
        }, this.FALLBACK_TIMEOUT_MS);
    }

    /**
     * Internal: Clear fallback timer (called on successful connection).
     */
    private clearFallbackTimer(): void {
        if (this.fallbackTimerId) {
            clearTimeout(this.fallbackTimerId);
            this.fallbackTimerId = null;
        }
    }
}

// Singleton instance - one subscription per browser session
export const studioEventStream = new StudioEventStreamServiceImpl();
